/* eslint-disable prefer-destructuring,func-names,prefer-arrow-callback,no-shadow */

import axios from 'axios';
import _ from 'lodash';
import { inspect } from 'util';

import { MODEL_CATEGORY_IDENTITY } from './categoryConstants.json';

const API_URL = process.env.API_URL;
const NODE_ENV = process.env.NODE_ENV;

/**
 * these methods connect/simulate REST APIs. its up to Redux to call and sync with them.
 *
 * @param bottle {Bottlejs}
 */

export default (bottle) => {
  bottle.factory('axios', () => axios);

  bottle.constant('CAT_URI', `${API_URL}/definition/categories`);
  bottle.constant('CAT_QUERY_URI', `${API_URL}/analytics/vbql`);

  bottle.factory('headers', () => token => ({
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }));

  bottle.factory('Category', () => class Category {
    constructor(props = {}) {
      this.query = props.query;
      this.notes = props.notes;
      this.tags = props.tags;
      this.categoryName = props.categoryName;
      this.version = props.version;
      this.categoryDescription = props.categoryDescription;
      if ('active' in props) {
        this.active = props.active;
      } else {
        this.active = true;
      }
    }

    get encodedID() {
      return encodeURIComponent(this.categoryName);
    }

    textToTags(text) {
      if (typeof text === 'string') {
        this.tags = _(text.split('\n'))
          .map(_.trim)
          .compact()
          .uniq()
          .sortBy(_.identity)
          .value();
      }
    }

    tagsToText() {
      return this.tags
        .filter(_.identity)
        .map(_.trim)
        .join('\n');
    }

    get query() {
      if (!this._query) {
        this._query = '';
      }
      return this._query;
    }

    set query(value) {
      if (typeof value === 'string') {
        this._query = value;
      }
    }

    get categoryName() {
      if (!this._categoryName) {
        this._categoryName = '';
      }
      return this._categoryName;
    }

    set categoryName(value) {
      if (typeof value === 'string') {
        this._categoryName = value;
      }
      this._categoryName = value;
    }


    get categoryDescription() {
      if (!this._categoryDescription) {
        this._categoryDescription = '';
      }
      return this._categoryDescription;
    }

    set categoryDescription(value) {
      if (typeof value === 'string') {
        this._categoryDescription = value;
      }
    }

    get version() {
      if (!this._version) {
        this._version = '1.0.0';
      }
      return this._version;
    }

    set version(value) {
      // @todo: validate format
      if (typeof value === 'string') {
        this._version = value;
      }
    }

    set notes(value) {
      if (typeof value === 'string') {
        this._notes = value;
      }
    }

    get notes() {
      if (!this._notes) {
        this._notes = '';
      }
      return this._notes;
    }

    get tags() {
      if (!this._tags) {
        this._tags = [];
      }
      return this._tags;
    }

    set tags(value) {
      // @TODO: Validate tags. may need a sub-class for content
      if (Array.isArray(value)) {
        this._tags = value.slice(0);
      } else {
        this._tags = [];
      }
    }

    toJSON() {
      return {
        categoryName: this.categoryName,
        query: this.query,
        notes: this.notes,
        categoryDescription: this.categoryDescription,
        tags: this.tags,
        version: this.version,
        active: this.active
      };
    }

    get active() {
      if (!('_active' in this)) {
        this._active = true;
      }
      return !!this._active;
    }

    set active(a) {
      this._active = !!a;
    }

    clone() {
      return new Category(this);
    }

    get errors() {
      const out = [];
      if (!_.trim(this.categoryName)) {
        out.push({ field: 'categoryName', error: 'required' });
      }
      if (!_.trim(this.query)) {
        out.push({
          field: 'query',
          error: 'required',
        });
      }
      return out.length ? out : false;
    }

    get isValid() {
      const errors = this.errors;
      return errors === false;
    }
  });

  /**
   * deprecated - a central store for model data.
   */
  bottle.factory('categoryModel', ({ recordToID, Category }) => {
    const model = {
      get data() {
        return model._data;
      },
      _data: [],
      setCategories(data) {
        model._data = data.map(record => new Category(record));
      },
      get(name) {
        return _.find(model.data, { [MODEL_CATEGORY_IDENTITY]: name });
      },
      add(record) {
        const cat = new Category(record);
        if (!cat[MODEL_CATEGORY_IDENTITY]) {
          throw new Error(`no ${MODEL_CATEGORY_IDENTITY}`);
        }
        model.removeIDs(cat);
        model._data = [...model.data, cat];
      },
      removeIDs(ids) {
        let list = Array.isArray(ids) ? ids : [ids];
        list = list.map(recordToID);
        model._data = _.reject(model.data, v => list.includes(v[MODEL_CATEGORY_IDENTITY]));
      },
    };
    return model;
  });

  bottle.factory(
    'categoryAddRecord',
    function ({
      CAT_URI, headers, Category, axios,
    }) {
      return async (record, token) => {
        const cat = record instanceof Category ? record : new Category(record);

        if (!_.get(cat, MODEL_CATEGORY_IDENTITY)) {
          throw new Error(`data missing ${MODEL_CATEGORY_IDENTITY}, ${record}`);
        }

        if (!token) {
          throw new Error('categoryAddRecord: no token');
        }

        return axios.put(
          `${CAT_URI}/${cat.encodedID}`,
          cat.toJSON(),
          { headers: headers(token) },
        );
      };
    },
  );

  bottle.factory(
    'recordToID',
    () => (record) => {
      if (record) {
        if (typeof record === 'string') {
          return record;
        }
        if (typeof record === 'object') {
          return _.get(record, MODEL_CATEGORY_IDENTITY, null);
        }
      }
      return null;
    },
  );

  bottle.factory(
    'categoryUpdateRecord',
    function ({
      recordToID,
      categoryRemoveRecords,
      categoryAddRecord,
    }) {
      return async (originalName, newRecord, token) => {
        if (!token) {
          throw new Error('categoryUpdateRecord: no token');
        }

        const oldID = recordToID(originalName);
        const out = await categoryAddRecord(newRecord, token);
        if (oldID !== newRecord.categoryName) {
          return categoryRemoveRecords(oldID, token);
        }
        return out;
      };
    },
  );

  bottle.factory(
    'categoryFetchAll',
    function ({
      headers, CAT_URI, axios,
    }) {
      return async (token) => {
        try {
          const config = { headers: headers(token) };
          const { data } = await axios.get(CAT_URI, config);
          return _.get(data, 'categories', []) || [];
        } catch (error) {
          console.log('category fetch error: <<<<<<', inspect(error));
          throw error;
        }
      };
    },
  );

  bottle.factory(
    'categoryFetchQuery',
    function ({
      headers, CAT_QUERY_URI, axios,
    }) {
      return async (query, token) => {
        if (!(query && token)) {
          return Promise.reject(new Error('categoryFetchQuery requires query and token'));
        }
        try {
          const response = await axios({
            url: CAT_QUERY_URI, method: 'post', data: query, headers: headers(token),
          });
          return _.get(response, 'data', []) || [];
        } catch (err) {
          const message = _.get(err, 'response.data.errors.0.error');
          if (message) throw new Error(message);
          throw err;
        }
      };
    },
  );

  bottle.factory(
    'categoryRemoveRecords',
    function ({
      CAT_URI, recordToID, headers, axios,
    }) {
      return async (records, token) => {
        const list = Array.isArray(records) ? records : [records];
        const ids = _(list).map(recordToID)
          .filter(_.identity)
          .uniq()
          .compact()
          .value();

        return await Promise.all(ids.map(id => axios.delete(
          `${CAT_URI}/${encodeURIComponent(id)}`,
          { headers: headers(token) },
        )));
      };
    },
  );

  bottle.factory(
    'categoryFetchRecord',
    function ({
      recordToID, CAT_URI, headers, axios, Category,
    }) {
      return async (recordOrName, token) => {
        const categoryName = recordToID(recordOrName);
        const cat = new Category({ categoryName });
        if (!categoryName) {
          throw new Error(`cannot get ${recordOrName}: no name`);
        }

        const { data } = await axios.get(
          `${CAT_URI}/${cat.encodedID}`,
          { headers: headers(token) },
        );
        return data;
      };
    },
  );
};
